/**
 * Inline Lucide-compatible icon set.
 * Avoids pulling in lucide-react for a handful of glyphs.
 */
type IconProps = { className?: string; strokeWidth?: number };

const base = (strokeWidth = 2) => ({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
});

export const Leaf = ({ className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
);

export const ArrowUpRight = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M7 7h10v10" />
    <path d="M7 17 17 7" />
  </svg>
);

export const ArrowDownRight = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="m7 7 10 10" />
    <path d="M17 7v10H7" />
  </svg>
);

export const MoveUpRight = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M13 5H19V11" />
    <path d="M19 5L5 19" />
  </svg>
);

export const Check = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const Menu = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M4 5h16" />
    <path d="M4 12h16" />
    <path d="M4 19h16" />
  </svg>
);

export const X = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

export const ExternalLink = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
);

export const TrendingUp = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M16 7h6v6" />
    <path d="m22 7-8.5 8.5-5-5L2 17" />
  </svg>
);

export const Scale = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M12 3v18" />
    <path d="m19 8 3 8a5 5 0 0 1-6 0zV7" />
    <path d="M3 7h1a17 17 0 0 0 8-2 17 17 0 0 0 8 2h1" />
    <path d="m5 8 3 8a5 5 0 0 1-6 0zV7" />
    <path d="M7 21h10" />
  </svg>
);

export const Lightbulb = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </svg>
);

export const Mail = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
    <rect x="2" y="4" width="20" height="16" rx="2" />
  </svg>
);

export const MapPin = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export const Calculator = ({ className, strokeWidth = 2 }: IconProps) => (
  <svg {...base(strokeWidth)} className={className}>
    <rect width="16" height="20" x="4" y="2" rx="2" />
    <line x1="8" x2="16" y1="6" y2="6" />
    <line x1="16" x2="16" y1="14" y2="18" />
    <path d="M16 10h.01" />
    <path d="M12 10h.01" />
    <path d="M8 10h.01" />
    <path d="M12 14h.01" />
    <path d="M8 14h.01" />
    <path d="M12 18h.01" />
    <path d="M8 18h.01" />
  </svg>
);
